// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import cn from 'classnames'
import PageContext from 'components/Page/context'
import { usePrevious } from 'hooks/utility'
import { lighten } from '@mui/material'
import { HeaderVariant, setHeaderVariant } from 'components/Layout/LayoutDucks'
import { useDispatchAction } from 'hooks/redux'
import { PageHeaderContext } from './PageHeaderContext'

let currentHeaderIsCompact
let currentHeaderIsAlternative
let resetCurrentHeaderTypeTimeoutHandle = 0
let currentHeaderHeight = 0

const useStyles = makeStyles()(theme => ({
  backdrop: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(${theme.palette.primary.main}, ${lighten(theme.palette.primary.main, 0.1)})`,
    transformOrigin: 'center top',
  },
  header: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(36),
    marginBottom: theme.spacing(-28),
    width: '100%',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
    '&.wide .header-content': {
      width: 1400,
    },
    [theme.breakpoints.down('sm')]: {
      paddingBottom: theme.spacing(16),
      marginBottom: theme.spacing(-16),
    },
  },
  alternative: {
    color: `${theme.palette.text.dark} !important`,
    '& > .header-content > *': {
      color: `${theme.palette.text.dark} !important`,
    },
    '& .alt-backdrop': {
      opacity: 1,
    },
  },
  altBackdrop: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: `linear-gradient(${'#FFF'}, #f0f0f0)`,
    borderBottom: `1px solid ${'rgba(255, 255, 255, 0.4)'}`,
    opacity: 0,
  },
  compact: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(21),
    marginBottom: theme.spacing(-18),
  },
  white: {
    '& .alt-backdrop': {
      borderBottom: 'none',
      background: '#FFF',
    },
  },
  headerContent: {
    width: 1000,
    maxWidth: 'calc(100% - 24px)',
    margin: '0 auto',
    textAlign: 'left' as const,
    position: 'relative' as const,
    zIndex: 4,
  },
  sidebarColumn: {
    '& .header-content': {
      margin: `0 ${theme.spacing(2)} 0 auto !important`,
    },
  },
}))

interface IHeaderProps {
  alternative?: boolean
  contentStyle?: React.CSSProperties
  compact?: boolean
  autoCompact?: boolean
  autoAlternative?: boolean
  className?: string
  wide?: boolean
  white?: boolean
  children?: React.ReactNode
  classes?: Partial<Record<string, string>>
}

function PageHeader(props: IHeaderProps) {
  const { classes } = useStyles()
  const {
    children,
    contentStyle,
    compact,
    autoCompact,
    alternative,
    autoAlternative,
    className,
    wide,
    white,
    ...restProps
  } = props

  const setVariant = useDispatchAction((newVariant: HeaderVariant) => setHeaderVariant({ variant: newVariant }))

  let backdrop = useRef<HTMLDivElement>(null)
  let container = useRef<HTMLDivElement>(null)
  let altBackdrop = useRef<HTMLDivElement>(null)

  const [isAlternative, setIsAlternative] = useState<boolean>(alternative)

  const { sidebar, sidebarMode } = React.useContext(PageContext)
  const prevAlternative = usePrevious(alternative)

  const acquireContainerRef = (div: HTMLDivElement) => {
    container.current = div
  }

  const acquireBackdropRef = (div: HTMLDivElement) => {
    backdrop.current = div
  }

  const acquireAltBackdropRef = (div: HTMLDivElement) => {
    altBackdrop.current = div
  }

  const getStyleName = () => {
    const styleNameList = [classes.header]

    if (compact || (autoCompact && currentHeaderIsCompact)) {
      styleNameList.push(classes.compact)
    }

    if (alternative || (autoAlternative && currentHeaderIsAlternative)) {
      styleNameList.push(classes.alternative)
    }

    if (className) {
      styleNameList.push(className)
    }

    if (white) {
      styleNameList.push(classes.white)
    }

    if (wide) {
      styleNameList.push('wide')
    }

    if (sidebar) {
      if (sidebarMode === 'column') {
        styleNameList.push(classes.sidebarColumn)
      }
    }

    return styleNameList.join(' ')
  }

  const actualizeBackdrop = useCallback(() => {
    const thisHeight = container.current.getBoundingClientRect().height

    if (backdrop.current?.animate) {
      const shouldAnimateHeight = (currentHeaderHeight && thisHeight !== currentHeaderHeight)
      const shouldAnimateColor = (alternative !== currentHeaderIsAlternative && currentHeaderIsAlternative !== undefined)

      if (shouldAnimateHeight) {
        backdrop.current.animate(
          [
            // This essentially animates the height change, only instead by using a vertical scaling transition, which is much faster.
            { transform: `scale3d(1, ${thisHeight > 0 ? (currentHeaderHeight / thisHeight) || 0 : 0}, 1)` },
            { transform: 'scale3d(1, 1, 1)' },
          ],
          {
            duration: 600,
            easing: 'ease',
          },
        )
      }

      if (shouldAnimateColor) {
        altBackdrop.current?.animate(
          [
            { opacity: currentHeaderIsAlternative ? 1 : 0 },
            { opacity: alternative ? 1 : 0 },
          ],
          {
            duration: 600,
            easing: 'ease',
          },
        )
      }
    }

    currentHeaderIsCompact = Boolean(compact)
    currentHeaderIsAlternative = Boolean(alternative)
    currentHeaderHeight = thisHeight
  }, [alternative, compact])

  useEffect(() => {
    window.clearTimeout(resetCurrentHeaderTypeTimeoutHandle)

    if (alternative && !prevAlternative) {
      setVariant('default')
      setIsAlternative(alternative)
    } else if (!alternative && prevAlternative) {
      setVariant('primary')
    }

    actualizeBackdrop()

    if (alternative) setVariant('default')

    return () => {
      window.clearTimeout(resetCurrentHeaderTypeTimeoutHandle)
      resetCurrentHeaderTypeTimeoutHandle = window.setTimeout(() => {
        currentHeaderHeight = 0
      }, 1000)

      if (isAlternative) {
        setVariant('primary')
      }
    }
  }, [actualizeBackdrop, alternative, isAlternative, prevAlternative, setVariant])

  return (
    <PageHeaderContext.Provider value={{ alternativeHeader: alternative }}>
      <div {...restProps} className={getStyleName()} ref={acquireContainerRef}>
        <div className={classes.backdrop} ref={acquireBackdropRef}>
          <div className={cn(classes.altBackdrop, 'alt-backdrop')} ref={acquireAltBackdropRef} />
        </div>

        <div className={cn(classes.headerContent, 'header-content')} style={contentStyle}>
          {children}
        </div>
      </div>
    </PageHeaderContext.Provider>
  )
}

export default PageHeader
